// ============ PRELOADER — timing captured before DOMContentLoaded ============
// Start the clock as early as possible (when this defer script executes,
// HTML is already parsed and the page is interactive). Capturing here rather
// than inside the DOMContentLoaded callback means the 300 ms minimum budget
// is measured from when the page is actually usable, not from some later point.
const _preloaderStart = Date.now();
const _PRELOADER_MIN_MS = 300; // just enough to prevent a jarring sub-100ms flash

function _initPreloader() {
  const preloader = document.getElementById('preloader');
  if (!preloader) return;

  function hidePreloader() {
    const minMs     = preloader.dataset.instant === 'true' ? 0 : _PRELOADER_MIN_MS;
    const elapsed   = Date.now() - _preloaderStart;
    const remaining = Math.max(0, minMs - elapsed);
    setTimeout(() => {
      preloader.classList.add('hidden');
      // Remove from layout exactly when the fade-out animation finishes —
      // avoids a fixed timer mismatch and keeps iOS toolbar colour in sync.
      preloader.addEventListener('animationend', function onEnd(e) {
        if (e.animationName === 'preloaderFadeOut') {
          preloader.removeEventListener('animationend', onEnd);
          preloader.style.display = 'none';
        }
      });
    }, remaining);
  }

  // The page is scrollable/interactive as soon as DOMContentLoaded fires.
  // There is NO reason to wait for window.load (which blocks until every
  // image and stylesheet is fully downloaded — that is what caused the
  // 7-second wait reported in India and other slower connections).
  // We call hidePreloader() directly here; it is already inside the
  // DOMContentLoaded callback so the DOM is ready.
  hidePreloader();
}

document.addEventListener('DOMContentLoaded', () => {
  _initPreloader(); // hide preloader — page is now scrollable

  // ============ FAVORITES - localStorage based ============
  let favorites = JSON.parse(localStorage.getItem('tcb_favorites') || '[]');

  function _tcbReadFavs() {
    try { const a = JSON.parse(localStorage.getItem('tcb_favorites') || '[]'); return Array.isArray(a) ? a : []; } catch (e) { return []; }
  }

  function toggleFav(handle, btn) {
    if (window.event) window.event.stopPropagation();
    favorites = _tcbReadFavs(); // re-read: the favorites page (and other tabs) may have changed it
    const idx = favorites.indexOf(handle);
    if (idx > -1) {
      favorites.splice(idx, 1);
      btn.classList.remove('active');
    } else {
      favorites.push(handle);
      btn.classList.add('active');
      tcbShowFavToast();
    }
    localStorage.setItem('tcb_favorites', JSON.stringify(favorites));
    updateFavCount();
    document.querySelectorAll(`.card-fav[data-handle="${handle}"]`).forEach(f => {
      f.classList.toggle('active', favorites.includes(handle));
    });
  }

  function updateFavCount() {
    favorites = _tcbReadFavs();
    const fc = document.getElementById('favCount');
    if (fc) {
      fc.textContent = favorites.length;
      fc.classList.toggle('show', favorites.length > 0);
    }
  }

  updateFavCount();
  document.querySelectorAll('.card-fav').forEach(btn => {
    const handle = btn.dataset.handle;
    if (favorites.includes(handle)) btn.classList.add('active');
  });

  // ============ FAVORITES TOAST — quiet confirmation, links to /pages/favorites ============
  // Shown only when a garment is ADDED to favorites (never on removal).
  // Self-contained: styles injected once, element created once and reused.
  let _favToastEl = null;
  let _favToastTimer = null;
  let _favToastDismiss = null;

  // Tapping anywhere on the page (or pressing Escape) dismisses the prompt early.
  // Attached on the tick AFTER the toast shows, so the heart-tap that opened it
  // doesn't immediately close it. Taps inside the toast are handled by the toast's
  // own click handler — this keeps the View link clickable (dismissing on
  // pointerdown would kill the link's click before it fires).
  function _favToastBindDismiss() {
    if (_favToastDismiss) return;
    const onPointer = e => {
      if (_favToastEl && _favToastEl.contains(e.target)) return;
      _favToastHide();
    };
    const onKey = e => { if (e.key === 'Escape') _favToastHide(); };
    const binding = { onPointer, onKey, active: false };
    _favToastDismiss = binding;
    setTimeout(() => {
      if (_favToastDismiss !== binding) return;
      binding.active = true;
      document.addEventListener('pointerdown', onPointer, { passive: true });
      document.addEventListener('keydown', onKey);
    }, 0);
  }

  function _favToastUnbindDismiss() {
    if (!_favToastDismiss) return;
    if (_favToastDismiss.active) {
      document.removeEventListener('pointerdown', _favToastDismiss.onPointer);
      document.removeEventListener('keydown', _favToastDismiss.onKey);
    }
    _favToastDismiss = null;
  }

  const _FAV_TOAST_LINES = [
    'A fine choice.',
    'Excellent taste.',
    'Consider it kept.',
    'One to remember.'
  ];

  function _favToastCss() {
    if (document.getElementById('tcbFavToastCss')) return;
    const s = document.createElement('style');
    s.id = 'tcbFavToastCss';
    s.textContent = `
      #tcbFavToast {
        position: fixed;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%) scale(.9);
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 18px 30px 18px 20px;
        background: linear-gradient(135deg, rgba(255, 255, 255, .66) 0%, rgba(255, 255, 255, .4) 55%, rgba(255, 255, 255, .56) 100%);
        -webkit-backdrop-filter: blur(30px) saturate(1.9);
        backdrop-filter: blur(30px) saturate(1.9);
        border: 1px solid rgba(255, 255, 255, .55);
        border-radius: 22px;
        box-shadow:
          0 24px 70px rgba(10, 15, 30, .22),
          0 4px 16px rgba(10, 15, 30, .08),
          inset 0 1px 0 rgba(255, 255, 255, .75),
          inset 0 -1px 0 rgba(255, 255, 255, .25);
        opacity: 0;
        pointer-events: none;
        z-index: 99999;
        max-width: calc(100vw - 32px);
        transition: opacity .45s cubic-bezier(.22, 1, .36, 1), transform .45s cubic-bezier(.34, 1.45, .64, 1);
      }
      #tcbFavToast::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: linear-gradient(115deg, rgba(255, 255, 255, .45) 0%, rgba(255, 255, 255, 0) 38%, rgba(255, 255, 255, 0) 62%, rgba(255, 255, 255, .18) 100%);
        pointer-events: none;
      }
      #tcbFavToast > * { position: relative; z-index: 1; }
      #tcbFavToast.show {
        opacity: 1;
        pointer-events: auto;
        transform: translate(-50%, -50%) scale(1);
      }
      #tcbFavToast .fav-toast-icon {
        flex: 0 0 auto;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        background: #0A0F1E;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      #tcbFavToast.show .fav-toast-icon svg {
        animation: tcbFavHeartPop .55s cubic-bezier(.34, 1.56, .64, 1) both;
      }
      #tcbFavToast .fav-toast-icon svg {
        width: 15px;
        height: 15px;
        fill: #FDFBF7;
        display: block;
      }
      #tcbFavToast .fav-toast-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
        white-space: nowrap;
      }
      #tcbFavToast .fav-toast-title {
        font-family: 'Cormorant Garamond', serif;
        font-style: italic;
        font-weight: 500;
        font-size: 17px;
        line-height: 1.2;
        color: #0A0F1E;
        letter-spacing: .01em;
      }
      #tcbFavToast .fav-toast-sub {
        font-family: 'Inter', sans-serif;
        font-size: 10px;
        font-weight: 500;
        letter-spacing: .14em;
        text-transform: uppercase;
        color: rgba(10, 15, 30, .62);
      }
      #tcbFavToast .fav-toast-sub a {
        color: #0A0F1E;
        text-decoration: none;
        border-bottom: 1px solid rgba(10, 15, 30, .35);
        padding-bottom: 1px;
        margin-left: 6px;
        transition: border-color .25s ease;
      }
      #tcbFavToast .fav-toast-sub a:hover {
        border-bottom-color: #0A0F1E;
      }
      @keyframes tcbFavHeartPop {
        0%   { transform: scale(.4); }
        55%  { transform: scale(1.25); }
        100% { transform: scale(1); }
      }
      @media (max-width: 480px) {
        #tcbFavToast { gap: 12px; padding: 15px 24px 15px 16px; border-radius: 18px; }
        #tcbFavToast .fav-toast-title { font-size: 16px; }
        #tcbFavToast .fav-toast-sub { font-size: 9px; letter-spacing: .12em; }
      }
      @media (prefers-reduced-motion: reduce) {
        #tcbFavToast { transition: opacity .2s ease; transform: translate(-50%, -50%); }
        #tcbFavToast.show { transform: translate(-50%, -50%); }
        #tcbFavToast.show .fav-toast-icon svg { animation: none; }
      }
    `;
    document.head.appendChild(s);
  }

  function _favToastBuild() {
    if (_favToastEl) return _favToastEl;
    _favToastCss();

    const el = document.createElement('div');
    el.id = 'tcbFavToast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');

    const icon = document.createElement('div');
    icon.className = 'fav-toast-icon';
    icon.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5.75 4.25A1.75 1.75 0 0 1 7.5 2.5h9a1.75 1.75 0 0 1 1.75 1.75V21.5L12 17.1l-6.25 4.4Z" stroke-linejoin="round"/></svg>';

    const text = document.createElement('div');
    text.className = 'fav-toast-text';

    const title = document.createElement('span');
    title.className = 'fav-toast-title';

    const sub = document.createElement('span');
    sub.className = 'fav-toast-sub';
    sub.appendChild(document.createTextNode('Saved to your favorites'));

    const link = document.createElement('a');
    link.href = '/pages/favorites';
    link.textContent = 'View';
    sub.appendChild(link);

    text.appendChild(title);
    text.appendChild(sub);
    el.appendChild(icon);
    el.appendChild(text);

    // Tapping the toast anywhere outside the link dismisses it quietly.
    el.addEventListener('click', e => {
      if (e.target.closest('a')) return;
      _favToastHide();
    });

    document.body.appendChild(el);
    _favToastEl = el;
    return el;
  }

  function _favToastHide() {
    if (_favToastTimer) { clearTimeout(_favToastTimer); _favToastTimer = null; }
    _favToastUnbindDismiss();
    if (_favToastEl) _favToastEl.classList.remove('show');
  }

  function tcbShowFavToast() {
    const el = _favToastBuild();
    el.querySelector('.fav-toast-title').textContent =
      _FAV_TOAST_LINES[Math.floor(Math.random() * _FAV_TOAST_LINES.length)];

    if (_favToastTimer) clearTimeout(_favToastTimer);

    // Restart the entrance (and heart pop) even on rapid successive adds.
    el.classList.remove('show');
    void el.offsetWidth; // reflow so the transition/animation re-runs
    el.classList.add('show');

    _favToastBindDismiss();
    _favToastTimer = setTimeout(_favToastHide, 3400);
  }

  // ============ NAV SCROLL ============
  // Nav always stays navy — add scrolled class immediately and keep it.
  // Element reference is cached once; { passive:true } lets the browser
  // optimise scroll rendering without waiting for JS.
  const _tcbNav = document.getElementById('navbar');
  if (_tcbNav) _tcbNav.classList.add('scrolled');
  window.addEventListener('scroll', () => {
    if (_tcbNav) _tcbNav.classList.add('scrolled');
  }, { passive: true });

  // ============ MOBILE MENU ============
  // The panel opens BELOW the top bar: the bar stays visible and the hamburger
  // (which morphs into an X) is the close control. Styling lives in
  // assets/tcb-styles.css → "MOBILE MENU — BELOW THE TOP BAR v5".
  let _tcbOverlay = null;
  let _tcbGlassAdded = false;

  // Bottom edge of the top bar = where the panel and its scrim begin.
  // Measured on every open, so the shipping strip and the notch are included.
  function tcbMenuTop() {
    const nav = document.getElementById('navbar');
    const top = nav ? Math.max(0, nav.getBoundingClientRect().bottom) : 56;
    document.documentElement.style.setProperty('--tcb-menu-top', top + 'px');
    if (_tcbOverlay) _tcbOverlay.style.top = top + 'px';
    return top;
  }

  // While the menu is open everything in the bar except the hamburger is inert
  // (the bar itself can no longer be inert — the hamburger closes the menu).
  function tcbNavInert(on) {
    const nav = document.getElementById('navbar');
    let el = document.getElementById('hamburger');
    while (nav && el && el !== nav && el.parentElement) {
      const sibs = el.parentElement.children;
      for (let i = 0; i < sibs.length; i++) {
        if (sibs[i] === el) continue;
        if (on) sibs[i].setAttribute('inert', '');
        else sibs[i].removeAttribute('inert');
      }
      el = el.parentElement;
    }
  }

  function tcbCloseMenu() {
    const m   = document.getElementById('mobileMenu');
    const b   = document.getElementById('hamburger');
    const nav = document.getElementById('navbar');
    const pg  = document.getElementById('tcb-page-content');
    if (m) m.classList.remove('open');
    if (b) { b.classList.remove('open'); b.setAttribute('aria-expanded', 'false'); }
    document.body.style.overflow = '';
    tcbNavInert(false);
    if (nav && _tcbGlassAdded) nav.classList.remove('tcb-glass');
    _tcbGlassAdded = false;
    if (pg)  pg.removeAttribute('inert');
    if (_tcbOverlay && _tcbOverlay.parentNode) {
      _tcbOverlay.parentNode.removeChild(_tcbOverlay);
      _tcbOverlay = null;
    }
  }

  function toggleMobile() {
    const m = document.getElementById('mobileMenu');
    const b = document.getElementById('hamburger');
    if (!m || !b) return;

    if (m.classList.contains('open')) {
      tcbCloseMenu();
      return;
    }

    // OPEN
    const nav = document.getElementById('navbar');
    const pg  = document.getElementById('tcb-page-content');
    // Homepage at the very top has a see-through bar: give it its glass while
    // the menu is open so bar + panel read as one surface (removed on close).
    if (nav && !nav.classList.contains('tcb-glass')) {
      nav.classList.add('tcb-glass');
      _tcbGlassAdded = true;
    }
    const top = tcbMenuTop();

    m.classList.add('open');
    b.classList.add('open');
    b.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    tcbNavInert(true);
    if (pg)  pg.setAttribute('inert', '');

    // Scrim built in JS — dims & blurs the page below the top bar (the bar
    // stays clear), and closes the menu when tapped
    _tcbOverlay = document.createElement('div');
    Object.assign(_tcbOverlay.style, {
      position: 'fixed',
      top: top + 'px', left: '0', right: '0', bottom: '0',
      zIndex: '1004',
      background: 'rgba(0,0,0,0.45)',
      backdropFilter: 'blur(4px)',
      webkitBackdropFilter: 'blur(4px)',
      cursor: 'pointer',
      webkitTapHighlightColor: 'transparent'
    });
    _tcbOverlay.addEventListener('click', tcbCloseMenu);
    _tcbOverlay.addEventListener('touchstart', function(e) {
      e.preventDefault();
      tcbCloseMenu();
    }, { passive: false });
    document.body.appendChild(_tcbOverlay);
  }

  // Keep the panel pinned under the bar if the viewport changes while open
  window.addEventListener('resize', function () {
    const m = document.getElementById('mobileMenu');
    if (m && m.classList.contains('open')) tcbMenuTop();
  });

  window.toggleMobile  = toggleMobile;
  window.tcbCloseMenu  = tcbCloseMenu;
  window.toggleFav = toggleFav;
  window.tcbUpdateFavCount = updateFavCount;


  // ============ BESPOKE CONFIGURATOR ============
  let cfgBase = 1500, cfgAdd = 0;

  function cfgPick(el, group) {
    el.closest('.cfg-step').querySelectorAll('.cfg-choice').forEach(c => c.classList.remove('active'));
    el.classList.add('active');
    updateCfgPrice();
  }

  function fabPick(el) {
    el.parentElement.querySelectorAll('.fab-swatch').forEach(s => s.classList.remove('active'));
    el.classList.add('active');
  }

  function updateCfgPrice() {
    const garment = document.querySelector('.cfg-step:first-child .cfg-choice.active');
    cfgBase = garment ? parseInt(garment.dataset.price) || 1500 : 1500;
    cfgAdd = 0;
    document.querySelectorAll('.cfg-choice.active[data-add]').forEach(el => {
      cfgAdd += parseInt(el.dataset.add) || 0;
    });
    const total = cfgBase + cfgAdd;
    const cfgPriceEl = document.getElementById('cfgPrice');
    const cfgTotalEl = document.getElementById('cfgTotal');
    if (cfgPriceEl) cfgPriceEl.textContent = '$' + total.toLocaleString('en-US');
    if (cfgTotalEl) cfgTotalEl.textContent = '$' + total.toLocaleString('en-US');
  }

  window.cfgPick = cfgPick;
  window.fabPick = fabPick;
  window.updateCfgPrice = updateCfgPrice;

  // ============================================================
  // IMAGE SWIPE ENGINE — PLP CARDS
  // ============================================================
  // Uses the original translating-track architecture (smooth, real-time feel).
  // GPU bleed is prevented by opacity:0 on non-adjacent slides — transparent
  // slides cannot visually bleed regardless of compositor behaviour, and
  // unlike visibility:hidden they still allow images to load (no blank flash).
  // ============================================================
  function initCardSwipe(container) {
    // Collection-preview cards on mobile: no image swipe, let carousel swipe pass through
    if (container.hasAttribute('data-cp-no-swipe')) {
      if (window.matchMedia('(max-width: 768px)').matches) {
        // Remove the marker so the carousel touchstart handler doesn't bail out
        container.removeAttribute('data-swipe-container');
        return;
      } else {
        // Desktop: extract slides stored in <template> wrappers, then init normally
        container.querySelectorAll('template.card-swipe-slide-tpl').forEach(function(tpl) {
          var frag = tpl.content.cloneNode(true);
          tpl.parentNode.insertBefore(frag, tpl);
          tpl.remove();
        });
      }
    }

    const track    = container.querySelector('[data-swipe-track]');
    const slides   = container.querySelectorAll('[data-swipe-slide]');
    const barThumb = container.querySelector('[data-swipe-bar-thumb]');
    const prevBtn  = container.querySelector('[data-swipe-prev]');
    const nextBtn  = container.querySelector('[data-swipe-next]');

    if (!track || slides.length <= 1) return;

    const total = slides.length;
    const SWIPE_THRESHOLD = 20;
    const SWIPE_VELOCITY  = 0.3;

    let current = 0;
    let startX = 0, startY = 0, startTime = 0;
    let isDragging = false, isHorizontal = null;
    let hasSwiped = false;
    let currentTranslate = 0;
    let startTranslate   = 0;

    // Disable GPU-layer promotion so overflow:hidden reliably clips the track
    track.style.willChange = 'auto';

    function updateBar(idx) {
      if (!barThumb) return;
      barThumb.style.width     = (100 / total) + '%';
      barThumb.style.transform = `translateX(${idx * 100}%)`;
    }

    // Only the active slide and its immediate neighbours are opaque.
    // All other slides are opacity:0 — transparent so they cannot bleed,
    // yet still present in the DOM so lazy-loaded images keep loading.
    function updateVisibility() {
      slides.forEach((s, i) => {
        const dist = Math.min(Math.abs(i - current), total - Math.abs(i - current));
        s.style.opacity       = dist <= 1 ? '1' : '0';
        s.style.pointerEvents = dist <= 1 ? '' : 'none';
      });
    }

    function goTo(index) {
      const wraps = index < 0 || index >= total;
      if (index < 0)      index = total - 1;
      if (index >= total) index = 0;
      current          = index;
      currentTranslate = -current * 100;
      updateVisibility();

      if (wraps) {
        // Instant snap for wrap-around — no fly-through of all slides
        track.style.transition = 'none';
        track.style.transform  = `translateX(${currentTranslate}%)`;
        requestAnimationFrame(() => requestAnimationFrame(() => { track.style.transition = ''; }));
      } else {
        track.style.transform = `translateX(${currentTranslate}%)`;
      }

      track.dataset.currentSlide = current;
      updateBar(current);
      container.dataset.userNavigated = 'true';
    }

    // Called by hover-swap after it writes track.dataset.currentSlide externally.
    // Syncs current/translate and moves the track to match.
    container._tcbSync = () => {
      const saved = parseInt(track.dataset.currentSlide);
      if (!isNaN(saved)) { current = saved; currentTranslate = -current * 100; }
      updateVisibility();
      track.style.transform = `translateX(${currentTranslate}%)`;
    };

    updateBar(0);
    updateVisibility();

    // Arrow buttons
    if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); goTo(current + 1); });

    // Suppress click-to-PDP after a horizontal swipe
    container.addEventListener('click', (e) => {
      if (hasSwiped) { e.preventDefault(); e.stopPropagation(); hasSwiped = false; }
    }, true);

    function prepareGesture() {
      // Sync current from dataset (covers hover-swap changes)
      const saved = parseInt(track.dataset.currentSlide);
      if (!isNaN(saved)) { current = saved; currentTranslate = -current * 100; }
      updateVisibility();
      track.style.transition = 'none';
      // Read the track's actual painted position so fast-swipe mid-animation
      // starts from the visual location, not a stale stored value
      const cs = window.getComputedStyle(track);
      if (cs.transform && cs.transform !== 'none') {
        try {
          const tx = new DOMMatrix(cs.transform).m41;
          if (container.offsetWidth) startTranslate = (tx / container.offsetWidth) * 100;
          else startTranslate = currentTranslate;
        } catch (_) { startTranslate = currentTranslate; }
      } else {
        startTranslate = currentTranslate;
      }
    }

    // Touch swipe
    container.addEventListener('touchstart', (e) => {
      prepareGesture();
      isDragging = true; isHorizontal = null; hasSwiped = false;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTime = Date.now();
    }, { passive: true });

    // NOT passive — needed for e.preventDefault() to block page scroll
    container.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      const diffX = e.touches[0].clientX - startX;
      const diffY = e.touches[0].clientY - startY;
      if (isHorizontal === null && (Math.abs(diffX) > 4 || Math.abs(diffY) > 4)) {
        isHorizontal = Math.abs(diffX) >= Math.abs(diffY);
      }
      if (isHorizontal === false) return;
      if (isHorizontal === true) {
        e.preventDefault();
        track.style.transform = `translateX(${startTranslate + (diffX / container.offsetWidth) * 100}%)`;
      }
    });

    container.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = '';
      if (!isHorizontal) return;
      hasSwiped = true;
      const diffX = e.changedTouches[0].clientX - startX;
      const isFastFlick = (Math.abs(diffX) / (Date.now() - startTime)) > SWIPE_VELOCITY;
      goTo(isFastFlick || Math.abs(diffX) > SWIPE_THRESHOLD
        ? (diffX < 0 ? current + 1 : current - 1)
        : current);
    });

    // Mouse drag for desktop
    container.addEventListener('mousedown', (e) => {
      prepareGesture();
      isDragging = true;
      startX = e.clientX; startTime = Date.now();
      e.preventDefault();
    });

    container.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      track.style.transform = `translateX(${startTranslate + ((e.clientX - startX) / container.offsetWidth) * 100}%)`;
    });

    container.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = '';
      const diff = e.clientX - startX;
      const isFastFlick = (Math.abs(diff) / (Date.now() - startTime)) > SWIPE_VELOCITY;
      goTo(diff < -SWIPE_THRESHOLD || (isFastFlick && diff < 0) ? current + 1
         : diff >  SWIPE_THRESHOLD || (isFastFlick && diff > 0) ? current - 1
         : current);
    });

    container.addEventListener('mouseleave', () => {
      if (isDragging) { isDragging = false; track.style.transition = ''; goTo(current); }
    });
  }

  // Init all PLP card swipe containers
  document.querySelectorAll('[data-swipe-container]').forEach(initCardSwipe);
  window.tcbInitCardSwipe = initCardSwipe; // for cards injected after load (favorites page)

  // ============================================================
  // IMAGE SWIPE ENGINE — PDP GALLERY  (seamless infinite loop)
  // ============================================================
  // Strategy: clone the first and last slides, wrapping the real slides:
  //   track DOM order → [clone-of-last | slide-0 | slide-1 | … | slide-N-1 | clone-of-first]
  //   internal pos   →       0              1        2              N             N+1
  //
  // Navigating left from pos=1 goes to pos=0 (clone of last) — smooth left-swipe animation.
  // After the CSS transition ends we silently jump to pos=N (the real last slide).
  // Same in reverse for navigating right from pos=N → pos=N+1 → jump to pos=1.
  // The user sees a continuous loop; no image flies backwards across the screen.
  // ============================================================
  function initPDPSwipe() {
    const container = document.querySelector('[data-pdp-swipe]');
    if (!container) return;

    const track    = container.querySelector('[data-pdp-track]');
    const origSlides = container.querySelectorAll('[data-pdp-slide]');
    const barThumb = container.querySelector('[data-pdp-bar-thumb]');
    const prevBtn  = container.querySelector('[data-pdp-prev]');
    const nextBtn  = container.querySelector('[data-pdp-next]');
    const thumbs   = document.querySelectorAll('.pdp-thumb');
    const total    = origSlides.length;

    if (!track || total <= 1) return;

    // --- Video slides (product-media videos appended after the images) ---
    // Play only the active slide's video, pause all others. When a product has
    // no videos this array is empty and syncVideos is a no-op — zero overhead.
    const slideVideos = [];
    origSlides.forEach((slide, i) => {
      const v = slide.querySelector('video');
      if (v) slideVideos.push({ idx: i, vid: v });
    });
    function syncVideos(activeIdx) {
      if (!slideVideos.length) return;
      let onVideo = false;
      slideVideos.forEach((o) => {
        if (o.idx === activeIdx) {
          onVideo = true;
          o.vid.muted = true; // guarantee muted so autoplay is always allowed
          const p = o.vid.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!o.vid.paused) {
          o.vid.pause();
        }
      });
      // Lets CSS hide image-only overlays (e.g. the zoom lens) while a video is active
      container.classList.toggle('pdp-video-active', onVideo);
    }
    window.tcbPdpSyncVideos = slideVideos.length ? syncVideos : null;

    // --- Build clone bookends ---
    // Clones do NOT carry [data-pdp-slide] so external code counting slides stays correct.
    const firstClone = origSlides[0].cloneNode(true);
    const lastClone  = origSlides[total - 1].cloneNode(true);
    firstClone.removeAttribute('data-pdp-slide'); firstClone.setAttribute('data-pdp-clone', 'true'); firstClone.setAttribute('aria-hidden', 'true');
    lastClone.removeAttribute('data-pdp-slide');  lastClone.setAttribute('data-pdp-clone', 'true');  lastClone.setAttribute('aria-hidden', 'true');

    // A clone must never contain a live <video>. iOS Safari positions a video's
    // hardware layer WITHOUT the track's translateX for the first frames after
    // the layer is created or revealed — and the pos-0 clone's untransformed
    // position is exactly the visible viewport, so its video painted on top of
    // the current slide the instant the user touched to swipe (seen as the
    // video flashing over image 1). The clone only ever shows during the 400ms
    // wrap animation, so its poster image is visually identical to the paused
    // video.
    [firstClone, lastClone].forEach((clone) => {
      clone.querySelectorAll('video').forEach((v) => {
        const img = document.createElement('img');
        img.src = v.getAttribute('poster') || '';
        img.alt = '';
        v.parentNode.replaceChild(img, v);
      });
    });

    track.appendChild(firstClone);
    track.insertBefore(lastClone, origSlides[0]);

    // Clones now stay permanently visible. They are image-only and sit outside
    // the clipped viewport except during wrap animations, so hiding them buys
    // nothing — and never toggling visibility means the browser never rebuilds
    // a compositor layer mid-gesture (that rebuild was the flash trigger).

    // pos  = position in the extended track (0 = lastClone, 1…N = real slides, N+1 = firstClone)
    // real = 0-based real slide index, used for bar / thumb updates
    let pos  = 1;   // start showing real slide 0
    let real = 0;
    let startX = 0, startY = 0, startTime = 0;
    let isDragging = false, isHorizontal = null;
    let currentTranslate = -100; // translateX for pos=1
    let startTranslate   = 0;
    const SWIPE_THRESHOLD = 20;   // px  — slow drag commits after this distance
    const SWIPE_VELOCITY  = 0.3;  // px/ms — fast flick commits regardless of distance

    // Position track at real slide 0 immediately, without animation
    track.style.transition = 'none';
    track.style.transform  = 'translateX(-100%)';
    // Un-suppress the CSS transition after the first paint
    requestAnimationFrame(() => requestAnimationFrame(() => { track.style.transition = ''; }));

    // --- Helpers ---
    function updateBar(idx) {
      if (!barThumb) return;
      barThumb.style.width     = (100 / total) + '%';
      barThumb.style.transform = `translateX(${idx * 100}%)`;
    }

    // Clones are never hidden any more (see above) — kept as a no-op so every
    // existing caller (goTo / syncAfterClone / edge-reveal paths) stays intact.
    function hideClones() {}

    // Called on transitionend after animating into a clone — silently jump to
    // the real counterpart. GUARDED: if a wrap animation is interrupted (a new
    // touch/drag before it finishes) the listener stays armed and would fire on
    // a LATER unrelated transition, teleporting the track to the wrong slide.
    // The jump therefore only runs when the track is parked exactly on a clone
    // position, derived from the actual transform — never from stale state.
    function syncAfterClone(e) {
      if (e && e.target !== track) return;   // ignore bubbled child transitions
      track.removeEventListener('transitionend', syncAfterClone);
      const t = track.style.transform;
      const onLastClone  = t === 'translateX(0%)';
      const onFirstClone = t === 'translateX(-' + ((total + 1) * 100) + '%)';
      if (!onLastClone && !onFirstClone) return;  // stale fire — no-op
      track.style.transition = 'none';
      pos = onLastClone ? total : 1;        // 0→last real, N+1→first real
      currentTranslate = -(pos * 100);
      track.style.transform = `translateX(${currentTranslate}%)`;
      requestAnimationFrame(() => requestAnimationFrame(() => { track.style.transition = ''; }));
    }

    // Navigate to an absolute pos in the extended track
    function goTo(newPos) {
      pos = newPos;
      currentTranslate = -(pos * 100);

      // Clones are permanently visible (image-only, clipped offscreen) — no
      // visibility management needed here any more; see clone setup above.

      track.style.transition = '';          // ensure CSS transition is active
      track.style.transform  = `translateX(${currentTranslate}%)`;

      // Determine the real index (clone positions map to their real counterpart for UI updates)
      real = (pos === 0) ? total - 1 : (pos === total + 1) ? 0 : pos - 1;
      track.dataset.currentSlide = real;
      updateBar(real);
      thumbs.forEach((t, i) => t.classList.toggle('active', i === real));
      syncVideos(real);

      // If we animated to a clone, schedule the invisible jump after the transition
      // finishes. Plain add (no {once}) — the handler removes itself, and duplicate
      // addEventListener calls with the same fn are deduped by the browser.
      if (pos === 0 || pos === total + 1) {
        track.addEventListener('transitionend', syncAfterClone);
      }
    }

    // Sync pos/currentTranslate from the real index stored by pdpThumbClick
    function syncFromDataset() {
      const saved = parseInt(track.dataset.currentSlide);
      if (!isNaN(saved)) {
        real = saved;
        pos  = saved + 1;                   // +1 because pos 0 is the prepended clone
        currentTranslate = -(pos * 100);
      }
    }

    // --- Arrow buttons ---
    function arrowNav(delta) { syncFromDataset(); goTo(pos + delta); }
    if (prevBtn) prevBtn.addEventListener('click', () => arrowNav(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => arrowNav(+1));

    // Init progress bar
    updateBar(0);

    // --- Touch swipe ---
    container.addEventListener('touchstart', (e) => {
      syncFromDataset();
      // Reveal adjacent clone if at an edge — user might drag into it
      if (pos === 1)     lastClone.style.visibility  = '';
      if (pos === total) firstClone.style.visibility = '';
      isDragging = true; isHorizontal = null;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTime = Date.now();
      startTranslate = currentTranslate;
      track.style.transition = 'none';
    }, { passive: true });

    // NOT passive — allows e.preventDefault() to block page scroll during horizontal swipe
    container.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      const diffX = e.touches[0].clientX - startX;
      const diffY = e.touches[0].clientY - startY;

      // Decide intent on first meaningful movement (4 px dead-zone)
      if (isHorizontal === null && (Math.abs(diffX) > 4 || Math.abs(diffY) > 4)) {
        isHorizontal = Math.abs(diffX) >= Math.abs(diffY);
      }
      if (isHorizontal === false) return; // vertical — let the browser scroll
      if (isHorizontal === true) {
        e.preventDefault();
        track.style.transform = `translateX(${startTranslate + (diffX / container.offsetWidth) * 100}%)`;
      }
    });

    container.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = '';
      if (!isHorizontal) return;

      const diffX    = e.changedTouches[0].clientX - startX;
      const elapsed  = Date.now() - startTime;
      const isFastFlick = (Math.abs(diffX) / elapsed) > SWIPE_VELOCITY;

      if (isFastFlick || Math.abs(diffX) > SWIPE_THRESHOLD) {
        goTo(diffX < 0 ? pos + 1 : pos - 1);
      } else {
        goTo(pos); // snap back
      }
    });

    // --- Mouse drag (desktop) ---
    container.addEventListener('mousedown', (e) => {
      syncFromDataset();
      // Reveal adjacent clone if at an edge — user might drag into it
      if (pos === 1)     lastClone.style.visibility  = '';
      if (pos === total) firstClone.style.visibility = '';
      isDragging = true;
      startX = e.clientX; startTime = Date.now();
      startTranslate = currentTranslate;
      track.style.transition = 'none';
      e.preventDefault();
    });

    container.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      track.style.transform = `translateX(${startTranslate + ((e.clientX - startX) / container.offsetWidth) * 100}%)`;
    });

    container.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = '';
      const diff = e.clientX - startX;
      const isFastFlick = (Math.abs(diff) / (Date.now() - startTime)) > SWIPE_VELOCITY;
      if      (diff < -SWIPE_THRESHOLD || (isFastFlick && diff < 0)) goTo(pos + 1);
      else if (diff >  SWIPE_THRESHOLD || (isFastFlick && diff > 0)) goTo(pos - 1);
      else goTo(pos);
    });

    container.addEventListener('mouseleave', () => {
      if (isDragging) { isDragging = false; track.style.transition = ''; goTo(pos); }
    });
  }

  initPDPSwipe();

  // ============ PRODUCT CARD HOVER IMAGE SWAP ============
  // On hover (fine-pointer only), advance to slide 1 if user hasn't already navigated.
  // On leave, return to slide 0 ONLY if user hasn't manually navigated via arrows/swipe.
  function initCardHoverSwap() {
    document.querySelectorAll('[data-hover-swap]').forEach(function(cardImg) {
      if (cardImg.closest('.pdp')) return;

      var swipeContainer = cardImg.querySelector('[data-swipe-container]');
      if (!swipeContainer) return;
      var track = swipeContainer.querySelector('[data-swipe-track]');
      var barThumb = swipeContainer.querySelector('[data-swipe-bar-thumb]');
      var barEl = swipeContainer.querySelector('[data-swipe-bar]');
      if (!track) return;

      var totalSlides = barEl ? parseInt(barEl.dataset.swipeTotal || '0') : 0;

      function updateBar(index) {
        if (!barThumb || totalSlides <= 1) return;
        barThumb.style.width = (100 / totalSlides) + '%';
        barThumb.style.transform = 'translateX(' + (index * 100) + '%)';
      }

      var isUserSwiping = false;

      swipeContainer.addEventListener('mousedown', function() { isUserSwiping = true; });
      swipeContainer.addEventListener('touchstart', function() { isUserSwiping = true; }, { passive: true });
      swipeContainer.addEventListener('mouseup', function() {
        setTimeout(function() { isUserSwiping = false; }, 100);
      });
      swipeContainer.addEventListener('touchend', function() {
        setTimeout(function() { isUserSwiping = false; }, 100);
      });

      if (window.matchMedia('(pointer: fine)').matches) {
        cardImg.addEventListener('mouseenter', function() {
          // If user has already manually navigated, don't override their choice
          if (isUserSwiping || swipeContainer.dataset.userNavigated === 'true') return;
          var current = parseInt(track.dataset.currentSlide || '0');
          if (current === 0) {
            track.dataset.currentSlide = '1';
            updateBar(1);
            if (swipeContainer._tcbSync) swipeContainer._tcbSync();
          }
        });

        cardImg.addEventListener('mouseleave', function() {
          // If user manually navigated with arrows/swipe, leave image where they put it
          if (isUserSwiping || swipeContainer.dataset.userNavigated === 'true') return;
          track.dataset.currentSlide = '0';
          updateBar(0);
          if (swipeContainer._tcbSync) swipeContainer._tcbSync();
        });
      }
    });
  }
  initCardHoverSwap();
  window.tcbInitCardHoverSwap = initCardHoverSwap; // for cards injected after load (favorites page)

  // ============ 360 SCROLL INTERACTION ============
  window.addEventListener('scroll', () => {
    const sec = document.querySelector('.pdp-360');
    if (!sec) return;
    const rect = sec.getBoundingClientRect();
    const progress = Math.max(0, Math.min(1, 1 - (rect.bottom / (window.innerHeight + rect.height))));
    const dotIdx = Math.min(7, Math.floor(progress * 8));
    sec.querySelectorAll('.rot-dot').forEach((d, i) => d.classList.toggle('active', i === dotIdx));
  });

  // Force Inter font on product description
  const pdpDesc = document.querySelector('.pdp-desc');
  if (pdpDesc) {
    [pdpDesc, ...pdpDesc.querySelectorAll('*')].forEach(el => {
      el.style.setProperty('font-family', "'Inter', -apple-system, sans-serif", 'important');
    });
  }

  // ============ IMAGE WARM-UP — hidden slides & carousel items ============
  // loading="lazy" images that sit inside a horizontally-clipped container
  // (the 2nd slide of a product-card swipe track, the off-screen items of the
  // homepage collection carousel) never intersect the viewport until they are
  // swiped/scrolled into view, so the browser only starts downloading them on
  // hover, swipe or arrow click — the user sees a blank frame for a moment.
  //
  // Fix: once the card / carousel is within ~400 px of the viewport, flip the
  // relevant <img> from lazy to eager. Per the HTML spec, setting loading to
  // "eager" on a lazy image immediately resumes its load, so the image is
  // already in cache by the time the user interacts. Cards far down the page
  // are left untouched, so bandwidth is only spent near where the user is.
  // Progressive: browsers without IntersectionObserver simply keep today's
  // behaviour.
  (function () {
    if (!('IntersectionObserver' in window)) return;

    // Never spend extra bandwidth on constrained connections: honour Data Saver
    // and skip on 2G-class networks (Network Information API; absent = proceed).
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && (conn.saveData || /(^|[^3-9])2g/.test(conn.effectiveType || ''))) return;

    // The 2nd-slide warm-up only pays off where hover-swap exists (mouse/trackpad).
    // Touch devices only reach slide 2 by deliberately swiping, so leave their
    // bandwidth untouched — behaviour on phones is exactly as before.
    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    function makeEager(img) {
      if (img && img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        var el = entry.target;
        if (el.classList.contains('cp-carousel-viewport')) {
          // Homepage collection carousel: warm every card image in the track
          el.querySelectorAll('img[loading="lazy"]').forEach(makeEager);
        } else {
          // Product card: warm the 2nd slide only (hover-swap / first swipe target)
          var slideImgs = el.querySelectorAll('[data-swipe-slide] img');
          if (slideImgs.length > 1) makeEager(slideImgs[1]);
        }
      });
    }, { rootMargin: '400px 0px' });

    if (canHover) {
      document.querySelectorAll('.card-img [data-swipe-track]').forEach(function (track) {
        io.observe(track.closest('.card-img') || track);
      });
    }
    document.querySelectorAll('.cp-carousel-viewport').forEach(function (vp) { io.observe(vp); });
  })();
  // ============ end IMAGE WARM-UP ============

  // ============ HERO VIDEO — RESUME ON TAB RETURN ============
  // Browsers (Chrome, Safari, Firefox) automatically pause autoplaying video
  // elements when a tab goes to the background via the Page Visibility API.
  // When the user returns, the video stays paused — we detect the two routes
  // back and call play() on the hero video in both cases.
  //
  // Route 1 — visibilitychange: tab was in background, user switches back.
  // Route 2 — pageshow (persisted): browser restored the page from its
  //   back/forward cache (bfcache) rather than reloading it. This is common
  //   on Safari and Chrome when navigating away then pressing Back. The page
  //   never re-ran DOMContentLoaded so autoplay never re-triggered.
  //
  // .catch() is required: browsers throw a DOMException if play() is called
  // while the document is still not fully interactive; silencing it is safe
  // because the video will resume correctly in the visibilitychange path.
  (function () {
    function resumeHeroVideo() {
      var v = document.querySelector('.hero-video');
      if (v && v.paused) v.play().catch(function () {});
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') resumeHeroVideo();
    });

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) resumeHeroVideo();
    });
  })();
  // ============ end HERO VIDEO RESUME ============
});
